import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const PrisonMnembaZanzibar = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">6 DAYS PRISON, MNEMBA & ZANZIBAR ISLAND TOUR</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          6 Days Prison, Mnemba & Zanzibar Island Tour
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on a delightful <strong>6 Days Prison, Mnemba & Zanzibar Island Tour</strong>, blending cultural exploration, marine adventures, and beach relaxation. Discover the historic Stone Town, snorkel in the vibrant waters of Mnemba Island, visit the giant tortoises of Prison Island, and unwind on Zanzibar’s pristine white sand beaches for an unforgettable tropical getaway.
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 6 Days Prison, Mnemba & Zanzibar Island Tour
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Arrive at Zanzibar Airport, where you will be warmly greeted and transferred to Stone Town. Check in at your hotel, enjoy welcome drinks, and take in the sunset views of this historic city before settling in for the night.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Horizon Hotel & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Prison Island Trip & Spice Tour</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a delicious breakfast, embark on a boat trip to Prison Island (Changuu Island) for snorkeling, swimming, and sunbathing. Explore the island’s historical sites, including the former prison and resident giant tortoises, and learn about its history tied to the slave trade. Enjoy a barbecue lunch featuring local spices, then proceed to a Spice Tour to discover Zanzibar’s tropical fruits and spices like cloves, cinnamon, and nutmeg. Return to your hotel for a restful evening.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Horizon Hotel & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Mnemba Island & Jozani Forest</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start your day with breakfast, followed by a full-day sea adventure to Mnemba Island, a marine conservation area renowned for snorkeling and swimming with dolphins. Enjoy sunbathing on its pristine beaches before heading to Jozani Forest to observe rare red colobus monkeys and lush biodiversity. Later, transfer to a beach resort on the East Coast for leisure and an overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Smiles Beach Resort or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Days 4-5: Free & Leisure Days</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Enjoy two leisurely days at your white sand beach resort after breakfast. Relax on the beach or partake in optional activities such as kayaking, fishing, or a guided village tour to Nungwi Beach, including a visit to a sea creature aquarium. These days offer flexibility to unwind or explore at your own pace.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Smiles Beach Resort or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 6: Last Shopping & Departure from Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, transfer to Stone Town for a final shopping experience at the bazaar and souvenir shops on Mizingani Street, exploring Zanzibar’s charming narrow streets. Afterward, transfer to Zanzibar Airport for your scheduled flight home.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Airport transfers in Zanzibar
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Prison Island trip, Spice Tour, Mnemba Island adventure, and Jozani Forest tour
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Snorkeling and dolphin swimming excursions
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International flights and visa fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities (e.g., Nungwi village tour, kayaking)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic beverages
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">6 Days Prison, Mnemba & Zanzibar Island Tour</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 6 Days, 5 Nights
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Zanzibar
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Airport pickup - Return to airport
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrisonMnembaZanzibar;